import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  User,
  MapPin,
  Lock,
  Shield,
  Download,
  Trash2,
  LogOut,
  Loader2,
  Database,
  FileText,
  Mail,
  CloudOff,
  Cloud,
  HardDrive,
  ChevronRight,
  Globe,
  Clock,
  Home,
  CreditCard,
  Plus,
  Edit2,
  AlertTriangle,
  Bell,
  CalendarDays,
  Check,
  ChevronsUpDown,
  Monitor,
  Smartphone,
  Tablet,
  X,
  KeyRound,
  ShieldCheck,
  ShieldX,
  UserPlus,
  History,
  ChevronDown,
} from "lucide-react";
import { format, differenceInDays, parseISO } from "date-fns";

// Clickable setting box component
const SettingBox = ({ icon: Icon, title, subtitle, onClick, iconColor = "text-sky-400", rightElement }) => (
  <button
    onClick={onClick}
    className="w-full p-4 glass-card rounded-xl flex items-center gap-4 hover:bg-white/5 transition-all text-left group"
  >
    <div className={`p-3 rounded-xl bg-white/5 ${iconColor}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-white font-medium">{title}</p>
      {subtitle && <p className="text-sm text-zinc-400 truncate">{subtitle}</p>}
    </div>
    {rightElement || <ChevronRight className="w-5 h-5 text-zinc-500 group-hover:text-zinc-300 transition-colors" />}
  </button>
);

// Status badge component
const StatusBadge = ({ connected }) => (
  <span className={`px-2 py-1 text-xs rounded-full ${connected ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
    {connected ? 'Connected' : 'Not connected'}
  </span>
);

const Personal = () => {
  const navigate = useNavigate();
  const { user, updateUser, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [dataSummary, setDataSummary] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteDataDialog, setShowDeleteDataDialog] = useState(false);
  const [driveConnected, setDriveConnected] = useState(false);
  const [driveLoading, setDriveLoading] = useState(false);
  
  // Travel Documents state
  const [travelDocuments, setTravelDocuments] = useState([]);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showDeleteDocumentDialog, setShowDeleteDocumentDialog] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [documentForm, setDocumentForm] = useState({
    document_type: "passport",
    document_name: "",
    document_number: "",
    issuing_country: "",
    expiry_date: "",
    notes: "",
    email_reminder: false,
    reminder_email: "",
    reminder_days_before: 270, // 9 months default for passport
  });

  const [profileForm, setProfileForm] = useState({
    name: "",
    home_country: "",
    home_city: "",
    home_timezone: null,
  });

  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  // Active sessions state
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [revokingSession, setRevokingSession] = useState(null);

  // Trusted devices state
  const [trustedDevices, setTrustedDevices] = useState([]);
  const [trustedDevicesLoading, setTrustedDevicesLoading] = useState(false);

  // Activity log state
  const [activityLog, setActivityLog] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityExpanded, setActivityExpanded] = useState(false);

  useEffect(() => {
    if (user) {
      setProfileForm({
        name: user.name || "",
        home_country: user.home_country || "",
        home_city: user.home_city || "",
        home_timezone: user.home_timezone || null,
      });
    }
  }, [user]);

  useEffect(() => {
    const loadInitialData = async () => {
      // Fetch all initial data in parallel (non-blocking)
      const fetches = [
        axios.get(`${API}/data/summary`, { withCredentials: true }).then(r => setDataSummary(r.data)).catch(() => {}),
        axios.get(`${API}/admin/check`, { withCredentials: true }).then(r => setIsAdmin(r.data.is_admin)).catch(() => setIsAdmin(false)),
        axios.get(`${API}/drive/status`, { withCredentials: true }).then(r => setDriveConnected(r.data.connected)).catch(() => {}),
        axios.get(`${API}/documents`, { withCredentials: true }).then(r => setTravelDocuments(r.data)).catch(() => {}),
        axios.get(`${API}/auth/sessions`, { withCredentials: true }).then(r => setSessions(r.data)).catch(() => {}).finally(() => setSessionsLoading(false)),
        axios.get(`${API}/auth/trusted-devices`, { withCredentials: true }).then(r => setTrustedDevices(r.data)).catch(() => {}).finally(() => setTrustedDevicesLoading(false)),
        axios.get(`${API}/auth/activity-log?limit=20&skip=0`, { withCredentials: true }).then(r => { setActivityLog(r.data.activities); setActivityTotal(r.data.total); }).catch(() => {}).finally(() => setActivityLoading(false)),
      ];
      await Promise.allSettled(fetches);
    };
    loadInitialData();

    const params = new URLSearchParams(window.location.search);
    if (params.get('drive_connected') === 'true') {
      toast.success("Google Drive connected successfully!");
      setDriveConnected(true);
      window.history.replaceState({}, '', '/personal');
    } else if (params.get('drive_error') === 'true') {
      toast.error("Failed to connect Google Drive");
      window.history.replaceState({}, '', '/personal');
    }
  }, []);

  const checkDriveStatus = async () => {
    try {
      const response = await axios.get(`${API}/drive/status`, { withCredentials: true });
      setDriveConnected(response.data.connected);
    } catch (error) {
    }
  };

  const handleConnectDrive = async () => {
    setDriveLoading(true);
    try {
      const response = await axios.get(`${API}/drive/connect`, { withCredentials: true });
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      if (error.response?.status === 503) {
        toast.error("Google Drive is not configured. Please contact support.");
      } else {
        toast.error(error.response?.data?.detail || "Failed to connect Google Drive");
      }
      setDriveLoading(false);
    }
  };

  const handleDisconnectDrive = async () => {
    setDriveLoading(true);
    try {
      await axios.delete(`${API}/drive/disconnect`, { withCredentials: true });
      setDriveConnected(false);
      toast.success("Google Drive disconnected");
    } catch (error) {
      toast.error("Failed to disconnect Google Drive");
    } finally {
      setDriveLoading(false);
    }
  };

  const checkAdminStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/check`, { withCredentials: true });
      setIsAdmin(response.data.is_admin);
    } catch (error) {
      setIsAdmin(false);
    }
  };

  const fetchDataSummary = async () => {
    try {
      const response = await axios.get(`${API}/data/summary`, { withCredentials: true });
      setDataSummary(response.data);
    } catch (error) {
    }
  };

  const handleUpdateProfile = async () => {
    setLoading(true);
    try {
      const response = await axios.put(`${API}/auth/profile`, profileForm, { withCredentials: true });
      updateUser(response.data);
      toast.success("Profile updated successfully!");
      setShowProfileModal(false);
      setShowLocationModal(false);
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }
    if (passwordForm.new_password.length < 12) {
      toast.error("Password must be at least 12 characters");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      }, { withCredentials: true });
      toast.success("Password changed successfully!");
      setShowPasswordModal(false);
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadData = async () => {
    try {
      const response = await axios.get(`${API}/data/download`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `explorio_data_${user.user_id}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Data downloaded successfully!");
    } catch (error) {
      toast.error("Failed to download data");
    }
  };

  const handleDeleteAllData = async () => {
    try {
      await axios.delete(`${API}/data/delete-all`, { withCredentials: true });
      toast.success("All data deleted successfully");
      logout();
    } catch (error) {
      toast.error("Failed to delete data");
    }
  };

  const handleLogout = () => {
    logout();
    toast.success("Logged out successfully");
  };

  // Active Sessions functions
  const fetchSessions = async () => {
    setSessionsLoading(true);
    try {
      const response = await axios.get(`${API}/auth/sessions`, { withCredentials: true });
      setSessions(response.data);
    } catch (error) {
    } finally {
      setSessionsLoading(false);
    }
  };

  const handleRevokeSession = async (sessionId) => {
    setRevokingSession(sessionId);
    try {
      await axios.delete(`${API}/auth/sessions/${sessionId}`, { withCredentials: true });
      toast.success("Session revoked");
      setSessions((prev) => prev.filter((s) => s.session_id !== sessionId));
    } catch (error) {
      toast.error("Failed to revoke session");
    } finally {
      setRevokingSession(null);
    }
  };

  const handleRevokeAllOtherSessions = async () => {
    try {
      await axios.delete(`${API}/auth/sessions`, { withCredentials: true });
      toast.success("All other sessions revoked");
      setSessions((prev) => prev.filter((s) => s.is_current));
    } catch (error) {
      toast.error("Failed to revoke sessions");
    }
  };

  const getDeviceIcon = (type) => {
    if (type === "Mobile") return Smartphone;
    if (type === "Tablet") return Tablet;
    return Monitor;
  };

  const formatSessionTime = (isoStr) => {
    if (!isoStr) return "Unknown";
    try {
      return format(parseISO(isoStr), "MMM d, yyyy 'at' HH:mm");
    } catch {
      return isoStr;
    }
  };

  // Trusted Devices functions
  const fetchTrustedDevices = async () => {
    setTrustedDevicesLoading(true);
    try {
      const response = await axios.get(`${API}/auth/trusted-devices`, { withCredentials: true });
      setTrustedDevices(response.data);
    } catch (error) {
    } finally {
      setTrustedDevicesLoading(false);
    }
  };

  const handleRevokeAllTrustedDevices = async () => {
    try {
      await axios.delete(`${API}/auth/trusted-devices`, { withCredentials: true });
      toast.success("All trusted devices revoked — 2FA will be required on next login");
      setTrustedDevices([]);
    } catch (error) {
      toast.error("Failed to revoke trusted devices");
    }
  };

  // Activity log functions
  const fetchActivityLog = async (loadMore = false) => {
    setActivityLoading(true);
    try {
      const skip = loadMore ? activityLog.length : 0;
      const response = await axios.get(`${API}/auth/activity-log?limit=20&skip=${skip}`, { withCredentials: true });
      if (loadMore) {
        setActivityLog((prev) => [...prev, ...response.data.activities]);
      } else {
        setActivityLog(response.data.activities);
      }
      setActivityTotal(response.data.total);
    } catch (error) {
    } finally {
      setActivityLoading(false);
    }
  };

  const activityConfig = {
    login_success: { icon: ShieldCheck, color: "text-emerald-400", bg: "bg-emerald-500/15", label: "Login" },
    login_failed: { icon: ShieldX, color: "text-red-400", bg: "bg-red-500/15", label: "Failed Login" },
    "2fa_failed": { icon: ShieldX, color: "text-orange-400", bg: "bg-orange-500/15", label: "2FA Failed" },
    account_created: { icon: UserPlus, color: "text-cyan-400", bg: "bg-cyan-500/15", label: "Account Created" },
    password_changed: { icon: KeyRound, color: "text-amber-400", bg: "bg-amber-500/15", label: "Password Changed" },
    password_change_failed: { icon: KeyRound, color: "text-red-400", bg: "bg-red-500/15", label: "Password Change Failed" },
    password_reset: { icon: KeyRound, color: "text-amber-400", bg: "bg-amber-500/15", label: "Password Reset" },
    trusted_device_added: { icon: Shield, color: "text-emerald-400", bg: "bg-emerald-500/15", label: "Device Trusted" },
    trusted_devices_revoked: { icon: ShieldX, color: "text-red-400", bg: "bg-red-500/15", label: "Devices Revoked" },
    session_revoked: { icon: X, color: "text-red-400", bg: "bg-red-500/15", label: "Session Revoked" },
    all_sessions_revoked: { icon: X, color: "text-red-400", bg: "bg-red-500/15", label: "All Sessions Revoked" },
  };

  const getActivityConfig = (type) => activityConfig[type] || { icon: History, color: "text-zinc-400", bg: "bg-white/5", label: type };

  const formatRelativeTime = (isoStr) => {
    if (!isoStr) return "";
    try {
      const date = new Date(isoStr);
      const now = new Date();
      const diffMs = now - date;
      const diffMin = Math.floor(diffMs / 60000);
      if (diffMin < 1) return "Just now";
      if (diffMin < 60) return `${diffMin}m ago`;
      const diffHr = Math.floor(diffMin / 60);
      if (diffHr < 24) return `${diffHr}h ago`;
      const diffDays = Math.floor(diffHr / 24);
      if (diffDays < 7) return `${diffDays}d ago`;
      return format(date, "MMM d, yyyy");
    } catch {
      return isoStr;
    }
  };

  // Travel Documents functions
  const fetchTravelDocuments = async () => {
    try {
      const response = await axios.get(`${API}/documents`, { withCredentials: true });
      setTravelDocuments(response.data);
    } catch (error) {
    }
  };

  // Date format conversion helpers
  const toDisplayDate = (isoDate) => {
    // Convert YYYY-MM-DD to DD.MM.YYYY
    if (!isoDate) return "";
    const parts = isoDate.split("-");
    if (parts.length === 3) {
      return `${parts[2]}.${parts[1]}.${parts[0]}`;
    }
    return isoDate;
  };

  const toIsoDate = (displayDate) => {
    // Convert DD.MM.YYYY to YYYY-MM-DD
    if (!displayDate) return "";
    const parts = displayDate.split(".");
    if (parts.length === 3 && parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return displayDate;
  };

  const handleSaveDocument = async () => {
    if (!documentForm.document_name || !documentForm.expiry_date) {
      toast.error("Please fill in document name and expiry date");
      return;
    }
    
    // Validate date format DD.MM.YYYY
    const dateParts = documentForm.expiry_date.split(".");
    if (dateParts.length !== 3 || dateParts[0].length !== 2 || dateParts[1].length !== 2 || dateParts[2].length !== 4) {
      toast.error("Please enter date in DD.MM.YYYY format (e.g., 31.12.2026)");
      return;
    }
    
    setLoading(true);
    try {
      // Convert to ISO format for backend
      const dataToSend = {
        ...documentForm,
        expiry_date: toIsoDate(documentForm.expiry_date)
      };
      
      if (editingDocument) {
        await axios.put(`${API}/documents/${editingDocument.document_id}`, dataToSend, { withCredentials: true });
        toast.success("Document updated successfully");
      } else {
        await axios.post(`${API}/documents`, dataToSend, { withCredentials: true });
        toast.success("Document added successfully");
      }
      fetchTravelDocuments();
      setShowDocumentModal(false);
      resetDocumentForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save document");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async () => {
    if (!documentToDelete) return;
    try {
      await axios.delete(`${API}/documents/${documentToDelete.document_id}`, { withCredentials: true });
      toast.success("Document deleted");
      fetchTravelDocuments();
      setShowDeleteDocumentDialog(false);
      setDocumentToDelete(null);
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const handleSendReminder = async (doc) => {
    try {
      const response = await axios.post(`${API}/documents/${doc.document_id}/send-reminder`, {}, { withCredentials: true });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reminder");
    }
  };

  const openEditDocument = (doc) => {
    setEditingDocument(doc);
    setDocumentForm({
      document_type: doc.document_type || "passport",
      document_name: doc.document_name || "",
      document_number: doc.document_number || "",
      issuing_country: doc.issuing_country || "",
      expiry_date: toDisplayDate(doc.expiry_date) || "",
      notes: doc.notes || "",
      email_reminder: doc.email_reminder || false,
      reminder_email: doc.reminder_email || "",
      reminder_days_before: doc.reminder_days_before || 30,
    });
    setShowDocumentModal(true);
  };

  const resetDocumentForm = () => {
    setEditingDocument(null);
    setDocumentForm({
      document_type: "passport",
      document_name: "",
      document_number: "",
      issuing_country: "",
      expiry_date: "",
      notes: "",
      email_reminder: false,
      reminder_email: "",
      reminder_days_before: 270, // 9 months default for passport
    });
  };

  const getExpiryStatus = (expiryDate, docType = null) => {
    try {
      const expiry = parseISO(expiryDate);
      const today = new Date();
      const daysUntilExpiry = differenceInDays(expiry, today);
      
      // For passports, use 9 months (270 days) as critical threshold
      const criticalDays = docType === "passport" ? 270 : 30;
      const warningDays = docType === "passport" ? 365 : 90;
      
      if (daysUntilExpiry < 0) {
        return { status: "expired", text: "Expired", color: "text-red-400 bg-red-500/20", days: daysUntilExpiry };
      } else if (daysUntilExpiry <= criticalDays) {
        const monthsLeft = Math.floor(daysUntilExpiry / 30);
        const text = docType === "passport" ? `${monthsLeft}mo (< 9mo)` : `${daysUntilExpiry} days`;
        return { status: "critical", text, color: "text-red-400 bg-red-500/20", days: daysUntilExpiry };
      } else if (daysUntilExpiry <= warningDays) {
        const monthsLeft = Math.floor(daysUntilExpiry / 30);
        const text = docType === "passport" ? `${monthsLeft} months` : `${daysUntilExpiry} days`;
        return { status: "warning", text, color: "text-amber-400 bg-amber-500/20", days: daysUntilExpiry };
      }
      const monthsLeft = Math.floor(daysUntilExpiry / 30);
      return { status: "ok", text: `${monthsLeft} months`, color: "text-emerald-400 bg-emerald-500/20", days: daysUntilExpiry };
    } catch {
      return { status: "unknown", text: "Unknown", color: "text-zinc-400 bg-zinc-500/20", days: 0 };
    }
  };

  const documentTypes = [
    { value: "passport", label: "Passport" },
    { value: "visa", label: "Visa" },
    { value: "id_card", label: "ID Card" },
    { value: "drivers_license", label: "Driver's License" },
    { value: "health_insurance", label: "Health Insurance" },
    { value: "travel_insurance", label: "Travel Insurance" },
    { value: "other", label: "Other" },
  ];

  const getDocumentIcon = (type) => {
    switch (type) {
      case "passport": return "🛂";
      case "visa": return "📋";
      case "id_card": return "🪪";
      case "drivers_license": return "🚗";
      case "health_insurance": return "🏥";
      case "travel_insurance": return "✈️";
      default: return "📄";
    }
  };

  // Generate comprehensive timezone list from Intl API
  const timezones = (() => {
    try {
      const zones = Intl.supportedValuesOf("timeZone");
      const now = new Date();
      return zones
        .map((zone) => {
          try {
            const fmt = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "shortOffset" });
            const parts = fmt.formatToParts(now);
            const offsetPart = parts.find((p) => p.type === "timeZoneName");
            const offset = offsetPart?.value || "";
            const city = zone.split("/").pop().replace(/_/g, " ");
            const region = zone.split("/")[0];
            return { value: zone, label: `${city} (${offset})`, region, city, offset };
          } catch {
            return null;
          }
        })
        .filter(Boolean)
        .sort((a, b) => a.city.localeCompare(b.city));
    } catch {
      // Fallback for older browsers
      return [
        "UTC", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
        "America/Anchorage", "Pacific/Honolulu", "Europe/London", "Europe/Paris", "Europe/Berlin",
        "Europe/Rome", "Europe/Madrid", "Europe/Vienna", "Europe/Amsterdam", "Europe/Prague",
        "Europe/Warsaw", "Europe/Stockholm", "Europe/Helsinki", "Europe/Tallinn", "Europe/Riga",
        "Europe/Vilnius", "Europe/Bucharest", "Europe/Sofia", "Europe/Athens", "Europe/Moscow",
        "Europe/Istanbul", "Europe/Kiev", "Europe/Oslo", "Europe/Copenhagen", "Europe/Brussels",
        "Europe/Zurich", "Europe/Budapest", "Europe/Belgrade", "Europe/Zagreb", "Europe/Sarajevo",
        "Europe/Tirane", "Europe/Lisbon", "Europe/Dublin", "Europe/Minsk",
        "Asia/Dubai", "Asia/Kolkata", "Asia/Singapore", "Asia/Hong_Kong", "Asia/Tokyo",
        "Asia/Seoul", "Asia/Shanghai", "Asia/Bangkok", "Asia/Jakarta", "Asia/Taipei",
        "Asia/Karachi", "Asia/Dhaka", "Asia/Tehran", "Asia/Riyadh", "Asia/Jerusalem",
        "Asia/Tbilisi", "Asia/Yerevan", "Asia/Baku", "Asia/Almaty", "Asia/Tashkent",
        "Australia/Sydney", "Australia/Melbourne", "Australia/Perth", "Pacific/Auckland",
        "Africa/Cairo", "Africa/Lagos", "Africa/Nairobi", "Africa/Johannesburg",
      ].map((z) => {
        const city = z === "UTC" ? "UTC" : z.split("/").pop().replace(/_/g, " ");
        return { value: z, label: city, region: z.split("/")[0], city };
      });
    }
  })();

  const [tzSearch, setTzSearch] = useState("");
  const [tzPopoverOpen, setTzPopoverOpen] = useState(false);
  const filteredTimezones = tzSearch
    ? timezones.filter((tz) => tz.label.toLowerCase().includes(tzSearch.toLowerCase()) || tz.value.toLowerCase().includes(tzSearch.toLowerCase()))
    : timezones;

  const getLocationDisplay = () => {
    if (profileForm.home_city && profileForm.home_country) {
      return `${profileForm.home_city}, ${profileForm.home_country}`;
    }
    if (profileForm.home_country) return profileForm.home_country;
    if (profileForm.home_city) return profileForm.home_city;
    return "Not set";
  };

  const getTimezoneDisplay = () => {
    if (!profileForm.home_timezone) return "Not set";
    const tz = timezones.find(t => t.value === profileForm.home_timezone);
    return tz?.label || profileForm.home_timezone;
  };

  return (
    <div className="page-container p-4 pt-6 pb-28" data-testid="personal">
      <div className="max-w-4xl mx-auto">
      {/* Profile Hero Banner */}
      <div className="relative h-36 sm:h-44 lg:h-52 overflow-hidden -mx-4 -mt-6 mb-6 rounded-b-2xl">
        <img src="https://images.unsplash.com/photo-1530789253388-582c481c54b0?w=1200&q=80" alt="" className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#080d19]/80 via-[#080d19]/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4 pb-5">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-sky-500 to-violet-500 flex items-center justify-center text-xl font-bold text-white shadow-lg shadow-sky-500/20 border-2 border-white/10">
              {user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <div>
              <h1 className="text-xl font-bold font-heading text-white drop-shadow-lg">{user?.name || "User"}</h1>
              <p className="text-sm text-slate-300/80 drop-shadow">{user?.email}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Travel Documents Section - Under name */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3 px-1">
          <h2 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Travel Documents</h2>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
              onClick={async () => {
                try {
                  const res = await axios.post(`${API}/documents/check-passport-validity`, {}, { withCredentials: true });
                  if (res.data.total_warnings > 0) {
                    toast.warning(`${res.data.total_warnings} passport warning(s) found! Check your email.`);
                  } else {
                    toast.success("All passports are valid for upcoming trips!");
                  }
                } catch (e) { toast.error("Failed to check passport validity"); }
              }}
              data-testid="check-passport-btn"
              title="Check passport validity for trips"
            >
              <Shield className="w-4 h-4 mr-1" />
              Check
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-sky-400 hover:text-sky-300 hover:bg-sky-500/10"
              onClick={() => { resetDocumentForm(); setShowDocumentModal(true); }}
              data-testid="add-document-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>
        </div>
        
        {travelDocuments.length === 0 ? (
          <div className="p-4 glass-card rounded-xl text-center">
            <CreditCard className="w-8 h-8 mx-auto mb-2 text-zinc-500" />
            <p className="text-sm text-zinc-400">No documents added yet</p>
            <p className="text-xs text-zinc-500 mt-1">Track passport, visa & more expiry dates</p>
          </div>
        ) : (
          <div className="space-y-2">
            {travelDocuments.map((doc) => {
              const expiryInfo = getExpiryStatus(doc.expiry_date, doc.document_type);
              return (
                <div
                  key={doc.document_id}
                  className="p-3 glass-card rounded-xl"
                  data-testid={`document-${doc.document_id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">{getDocumentIcon(doc.document_type)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-white font-medium truncate">{doc.document_name}</p>
                        {(expiryInfo.status === "expired" || expiryInfo.status === "critical") && (
                          <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-zinc-400">
                          {documentTypes.find(t => t.value === doc.document_type)?.label || doc.document_type}
                        </span>
                        {doc.issuing_country && (
                          <>
                            <span className="text-zinc-600">•</span>
                            <span className="text-xs text-zinc-400">{doc.issuing_country}</span>
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <CalendarDays className="w-3 h-3 text-zinc-500" />
                        <span className="text-xs text-zinc-400">
                          Expires: {doc.expiry_date ? (() => {
                            try {
                              return format(parseISO(doc.expiry_date), "dd.MM.yyyy");
                            } catch {
                              return doc.expiry_date;
                            }
                          })() : "Not set"}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${expiryInfo.color}`}>
                          {expiryInfo.text}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => openEditDocument(doc)}
                        className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4 text-zinc-400" />
                      </button>
                      <button
                        onClick={() => handleSendReminder(doc)}
                        className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                        title="Send Reminder"
                      >
                        <Bell className="w-4 h-4 text-amber-400" />
                      </button>
                      <button
                        onClick={() => { setDocumentToDelete(doc); setShowDeleteDocumentDialog(true); }}
                        className="p-1.5 hover:bg-red-500/10 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </div>
                  {doc.email_reminder && doc.reminder_email && (
                    <div className="mt-2 pt-2 border-t border-white/5 flex items-center gap-2">
                      <Mail className="w-3 h-3 text-sky-400" />
                      <span className="text-xs text-zinc-400">
                        Reminder: {doc.reminder_email} ({doc.reminder_days_before} days before)
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Logout - Under Travel Documents */}
      <div className="mb-6">
        <SettingBox
          icon={LogOut}
          title="Logout"
          subtitle="Sign out of your account"
          onClick={handleLogout}
          iconColor="text-zinc-400"
        />
      </div>

      {/* Profile Section */}
      <div className="mb-6">
        <h2 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3 px-1">Profile</h2>
        <div className="space-y-2">
          <SettingBox
            icon={User}
            title="Name"
            subtitle={profileForm.name || "Not set"}
            onClick={() => setShowProfileModal(true)}
            iconColor="text-sky-400"
          />
          <SettingBox
            icon={Home}
            title="Home Location"
            subtitle={getLocationDisplay()}
            onClick={() => setShowLocationModal(true)}
            iconColor="text-emerald-400"
          />
          <SettingBox
            icon={Clock}
            title="Timezone"
            subtitle={getTimezoneDisplay()}
            onClick={() => setShowLocationModal(true)}
            iconColor="text-amber-400"
          />
        </div>
      </div>

      {/* Connections Section */}
      <div className="mb-6">
        <h2 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3 px-1">Connections</h2>
        <div className="space-y-2">
          <SettingBox
            icon={HardDrive}
            title="Google Drive"
            subtitle={driveConnected ? "Import files from Drive" : "Connect to import files"}
            onClick={driveConnected ? handleDisconnectDrive : handleConnectDrive}
            iconColor="text-blue-400"
            rightElement={
              driveLoading ? (
                <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
              ) : (
                <StatusBadge connected={driveConnected} />
              )
            }
          />
        </div>
      </div>

      {/* Security Section */}
      <div className="mb-6">
        <h2 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3 px-1">Security</h2>
        <div className="space-y-2">
          <SettingBox
            icon={Lock}
            title="Change Password"
            subtitle={user?.has_password ? "Update your password" : "Not available for Google accounts"}
            onClick={() => user?.has_password && setShowPasswordModal(true)}
            iconColor="text-amber-400"
          />
        </div>

        {/* Active Sessions */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-3 px-1">
            <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Active Sessions</h3>
            {sessions.filter((s) => !s.is_current).length > 0 && (
              <button
                onClick={handleRevokeAllOtherSessions}
                className="text-xs text-red-400 hover:text-red-300 transition-colors"
                data-testid="revoke-all-sessions-btn"
              >
                Revoke all others
              </button>
            )}
          </div>
          {sessionsLoading ? (
            <div className="p-4 glass-card rounded-xl flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="p-4 glass-card rounded-xl text-center">
              <Monitor className="w-8 h-8 mx-auto mb-2 text-zinc-500" />
              <p className="text-sm text-zinc-400">No active sessions</p>
            </div>
          ) : (
            <div className="space-y-2">
              {sessions.map((session) => {
                const DeviceIcon = getDeviceIcon(session.device_type);
                return (
                  <div
                    key={session.session_id}
                    className="p-3 glass-card rounded-xl"
                    data-testid={`session-${session.session_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2.5 rounded-xl ${session.is_current ? "bg-cyan-500/15 text-cyan-400" : "bg-white/5 text-zinc-400"}`}>
                        <DeviceIcon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-white font-medium text-sm truncate">
                            {session.browser} on {session.os}
                          </p>
                          {session.is_current && (
                            <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-cyan-500/20 text-cyan-400 flex-shrink-0" data-testid="current-session-badge">
                              Current
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-xs text-zinc-500">
                          <span>{session.ip}</span>
                          {session.city && session.city !== "Unknown" && (
                            <>
                              <span className="text-zinc-700">·</span>
                              <span>{session.city}, {session.country}</span>
                            </>
                          )}
                        </div>
                        <p className="text-xs text-zinc-600 mt-1">
                          {formatSessionTime(session.login_at)}
                        </p>
                      </div>
                      {!session.is_current && (
                        <button
                          onClick={() => handleRevokeSession(session.session_id)}
                          disabled={revokingSession === session.session_id}
                          className="p-1.5 hover:bg-red-500/10 rounded-lg transition-colors flex-shrink-0"
                          title="Revoke session"
                          data-testid={`revoke-session-${session.session_id}`}
                        >
                          {revokingSession === session.session_id ? (
                            <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />
                          ) : (
                            <X className="w-4 h-4 text-red-400" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Trusted Devices Section */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3 px-1">
          <h2 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Trusted Devices</h2>
          {trustedDevices.length > 0 && (
            <button
              onClick={handleRevokeAllTrustedDevices}
              className="text-xs text-red-400 hover:text-red-300 transition-colors"
              data-testid="revoke-all-trusted-btn"
            >
              Revoke all
            </button>
          )}
        </div>
        {trustedDevicesLoading ? (
          <div className="p-4 glass-card rounded-xl flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
          </div>
        ) : trustedDevices.length === 0 ? (
          <div className="p-4 glass-card rounded-xl text-center">
            <Shield className="w-8 h-8 mx-auto mb-2 text-zinc-500" />
            <p className="text-sm text-zinc-400">No trusted devices</p>
            <p className="text-xs text-zinc-500 mt-1">Check "Remember this device" during 2FA to add one</p>
          </div>
        ) : (
          <div className="space-y-2">
            {trustedDevices.map((device, idx) => {
              const DeviceIcon = getDeviceIcon(device.device_type);
              return (
                <div
                  key={idx}
                  className="p-3 glass-card rounded-xl"
                  data-testid={`trusted-device-${idx}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2.5 rounded-xl bg-emerald-500/15 text-emerald-400">
                      <DeviceIcon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium text-sm truncate">
                        {device.browser} on {device.os}
                      </p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-zinc-500">
                        <span>{device.ip}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-zinc-600">
                        <span>Added {formatSessionTime(device.created_at)}</span>
                        <span className="text-zinc-700">·</span>
                        <span>Expires {formatSessionTime(device.expires_at)}</span>
                      </div>
                    </div>
                    <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-emerald-500/20 text-emerald-400 flex-shrink-0">
                      Trusted
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Activity Log Section */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3 px-1">
          <h2 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Activity Log</h2>
          {activityLog.length > 0 && (
            <button
              onClick={() => setActivityExpanded(!activityExpanded)}
              className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors flex items-center gap-1"
              data-testid="toggle-activity-log"
            >
              {activityExpanded ? "Show less" : `Show all (${activityTotal})`}
              <ChevronDown className={`w-3 h-3 transition-transform ${activityExpanded ? "rotate-180" : ""}`} />
            </button>
          )}
        </div>
        {activityLoading && activityLog.length === 0 ? (
          <div className="p-4 glass-card rounded-xl flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
          </div>
        ) : activityLog.length === 0 ? (
          <div className="p-4 glass-card rounded-xl text-center">
            <History className="w-8 h-8 mx-auto mb-2 text-zinc-500" />
            <p className="text-sm text-zinc-400">No activity yet</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {(activityExpanded ? activityLog : activityLog.slice(0, 5)).map((evt) => {
              const config = getActivityConfig(evt.event_type);
              const Icon = config.icon;
              return (
                <div
                  key={evt.activity_id}
                  className="p-2.5 glass-card rounded-xl"
                  data-testid={`activity-${evt.activity_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${config.bg}`}>
                      <Icon className={`w-4 h-4 ${config.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-white font-medium text-sm">{config.label}</p>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                          evt.status === "success" ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"
                        }`}>
                          {evt.status === "success" ? "OK" : "FAIL"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-xs text-zinc-500">
                        {evt.details && <span className="truncate">{evt.details}</span>}
                        {evt.ip && evt.ip !== "Unknown" && (
                          <>
                            <span className="text-zinc-700">·</span>
                            <span className="flex-shrink-0">{evt.ip}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-zinc-600 flex-shrink-0">{formatRelativeTime(evt.timestamp)}</span>
                  </div>
                </div>
              );
            })}
            {activityExpanded && activityLog.length < activityTotal && (
              <button
                onClick={() => fetchActivityLog(true)}
                disabled={activityLoading}
                className="w-full py-2 text-xs text-cyan-400 hover:text-cyan-300 transition-colors glass-card rounded-xl"
                data-testid="load-more-activity"
              >
                {activityLoading ? "Loading..." : "Load more"}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Data & Privacy Section */}
      <div className="mb-6">
        <h2 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3 px-1">Data & Privacy</h2>
        <div className="space-y-2">
          {dataSummary && (
            <div className="p-4 glass-card rounded-xl mb-2">
              <div className="flex items-center gap-2 mb-3">
                <Database className="w-4 h-4 text-sky-400" />
                <span className="text-sm font-medium text-white">Your Data</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-2 bg-white/5 rounded-lg">
                  <p className="text-lg font-bold text-white">{dataSummary.mongodb_data?.trips || 0}</p>
                  <p className="text-xs text-zinc-400">Trips</p>
                </div>
                <div className="p-2 bg-white/5 rounded-lg">
                  <p className="text-lg font-bold text-white">{dataSummary.mongodb_data?.tickets || 0}</p>
                  <p className="text-xs text-zinc-400">Tickets</p>
                </div>
                <div className="p-2 bg-white/5 rounded-lg">
                  <p className="text-lg font-bold text-white">{dataSummary.mongodb_data?.bookings || 0}</p>
                  <p className="text-xs text-zinc-400">Bookings</p>
                </div>
              </div>
            </div>
          )}
          <SettingBox
            icon={FileText}
            title="Privacy Policy"
            subtitle="GDPR & data protection"
            onClick={() => navigate("/privacy")}
            iconColor="text-violet-400"
          />
          <SettingBox
            icon={Download}
            title="Download My Data"
            subtitle="Export all your data as JSON"
            onClick={handleDownloadData}
            iconColor="text-emerald-400"
          />
          <SettingBox
            icon={Trash2}
            title="Delete All Data"
            subtitle="Permanently remove your account"
            onClick={() => setShowDeleteDataDialog(true)}
            iconColor="text-red-400"
          />
        </div>
      </div>

      {/* Admin Section */}
      {isAdmin && (
        <div className="mb-6">
          <h2 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3 px-1">Admin</h2>
          <SettingBox
            icon={Shield}
            title="Admin Panel"
            subtitle="Manage users and view analytics"
            onClick={() => navigate("/admin")}
            iconColor="text-violet-400"
          />
        </div>
      )}

      {/* Edit Name Modal */}
      <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
        <DialogContent className="glass-card border-white/10">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Name</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Name</Label>
              <Input
                value={profileForm.name}
                onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                className="glass-input"
                placeholder="Your name"
                data-testid="profile-name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProfileModal(false)} className="btn-outline">
              Cancel
            </Button>
            <Button onClick={handleUpdateProfile} className="btn-primary" disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Location Modal */}
      <Dialog open={showLocationModal} onOpenChange={setShowLocationModal}>
        <DialogContent className="glass-card border-white/10">
          <DialogHeader>
            <DialogTitle className="font-heading">Home Location</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Country</Label>
              <Input
                value={profileForm.home_country}
                onChange={(e) => setProfileForm({ ...profileForm, home_country: e.target.value })}
                className="glass-input"
                placeholder="e.g., Germany"
                data-testid="profile-home-country"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">City</Label>
              <Input
                value={profileForm.home_city}
                onChange={(e) => setProfileForm({ ...profileForm, home_city: e.target.value })}
                className="glass-input"
                placeholder="e.g., Berlin"
                data-testid="profile-home-city"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Timezone</Label>
              <Popover open={tzPopoverOpen} onOpenChange={setTzPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={tzPopoverOpen}
                    className="glass-input w-full justify-between font-normal"
                    data-testid="profile-timezone"
                  >
                    {profileForm.home_timezone
                      ? timezones.find((tz) => tz.value === profileForm.home_timezone)?.label || profileForm.home_timezone
                      : "Search timezone..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="glass-card border-white/10 p-0 w-[320px]" align="start">
                  <div className="p-2 border-b border-white/10">
                    <Input
                      placeholder="Search city or timezone..."
                      value={tzSearch}
                      onChange={(e) => setTzSearch(e.target.value)}
                      className="glass-input h-9 text-sm"
                      autoFocus
                      data-testid="tz-search-input"
                    />
                  </div>
                  <div className="max-h-[220px] overflow-y-auto p-1">
                    {filteredTimezones.length === 0 ? (
                      <p className="text-zinc-500 text-sm py-4 text-center">No timezone found</p>
                    ) : (
                      filteredTimezones.slice(0, 50).map((tz) => (
                        <button
                          key={tz.value}
                          onClick={() => {
                            setProfileForm({ ...profileForm, home_timezone: tz.value });
                            setTzPopoverOpen(false);
                            setTzSearch("");
                          }}
                          className={`w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-2 hover:bg-white/10 transition-colors ${
                            profileForm.home_timezone === tz.value ? "bg-white/5 text-emerald-400" : "text-zinc-300"
                          }`}
                          data-testid={`tz-option-${tz.value}`}
                        >
                          {profileForm.home_timezone === tz.value && (
                            <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                          )}
                          <span className={profileForm.home_timezone === tz.value ? "" : "ml-5.5"}>{tz.label}</span>
                        </button>
                      ))
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLocationModal(false)} className="btn-outline">
              Cancel
            </Button>
            <Button onClick={handleUpdateProfile} className="btn-primary" disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="glass-card border-white/10">
          <DialogHeader>
            <DialogTitle className="font-heading">Change Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Current Password</Label>
              <Input
                type="password"
                className="glass-input"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">New Password</Label>
              <Input
                type="password"
                className="glass-input"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Confirm New Password</Label>
              <Input
                type="password"
                className="glass-input"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordModal(false)} className="btn-outline">
              Cancel
            </Button>
            <Button onClick={handleChangePassword} className="btn-primary" disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Change Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Data Confirmation */}
      <AlertDialog open={showDeleteDataDialog} onOpenChange={setShowDeleteDataDialog}>
        <AlertDialogContent className="glass-card border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400">Delete All Data</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This action cannot be undone. This will permanently delete your account and all
              associated data including trips, bookings, tickets, and chat history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-outline">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAllData}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Delete Everything
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Travel Document Modal */}
      <Dialog open={showDocumentModal} onOpenChange={setShowDocumentModal}>
        <DialogContent className="glass-card border-white/10 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingDocument ? "Edit Document" : "Add Travel Document"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Document Type</Label>
              <Select
                value={documentForm.document_type}
                onValueChange={(value) => setDocumentForm({ 
                  ...documentForm, 
                  document_type: value,
                  // Set default reminder to 9 months for passports
                  reminder_days_before: value === "passport" ? 270 : documentForm.reminder_days_before
                })}
              >
                <SelectTrigger className="glass-input" data-testid="document-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="glass-card border-white/10">
                  {documentTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Document Name *</Label>
              <Input
                value={documentForm.document_name}
                onChange={(e) => setDocumentForm({ ...documentForm, document_name: e.target.value })}
                className="glass-input"
                placeholder="e.g., German Passport"
                data-testid="document-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-zinc-300">Document Number</Label>
                <Input
                  value={documentForm.document_number}
                  onChange={(e) => setDocumentForm({ ...documentForm, document_number: e.target.value })}
                  className="glass-input"
                  placeholder="C12345678"
                  data-testid="document-number"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Issuing Country</Label>
                <Input
                  value={documentForm.issuing_country}
                  onChange={(e) => setDocumentForm({ ...documentForm, issuing_country: e.target.value })}
                  className="glass-input"
                  placeholder="Germany"
                  data-testid="document-country"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Expiry Date * (DD.MM.YYYY)</Label>
              <Input
                type="text"
                value={documentForm.expiry_date}
                onChange={(e) => {
                  let val = e.target.value;
                  // Auto-add dots as user types
                  val = val.replace(/[^\d.]/g, ''); // Only digits and dots
                  if (val.length === 2 && !val.includes('.')) val += '.';
                  if (val.length === 5 && val.split('.').length === 2) val += '.';
                  setDocumentForm({ ...documentForm, expiry_date: val });
                }}
                className="glass-input"
                placeholder="31.12.2026"
                maxLength={10}
                data-testid="document-expiry"
              />
              <p className="text-xs text-zinc-500">Format: DD.MM.YYYY (e.g., 31.12.2026)</p>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Notes</Label>
              <Input
                value={documentForm.notes}
                onChange={(e) => setDocumentForm({ ...documentForm, notes: e.target.value })}
                className="glass-input"
                placeholder="Any additional notes"
                data-testid="document-notes"
              />
            </div>
            
            {/* Email Reminder Section */}
            <div className="p-3 bg-white/5 rounded-xl space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="email-reminder"
                  checked={documentForm.email_reminder}
                  onChange={(e) => setDocumentForm({ ...documentForm, email_reminder: e.target.checked })}
                  className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-sky-500 focus:ring-sky-500"
                />
                <Label htmlFor="email-reminder" className="text-zinc-300 cursor-pointer">
                  Send email reminder before expiry
                </Label>
              </div>
              
              {documentForm.email_reminder && (
                <>
                  <div className="space-y-2">
                    <Label className="text-zinc-400 text-sm">Reminder Email</Label>
                    <Input
                      type="email"
                      value={documentForm.reminder_email}
                      onChange={(e) => setDocumentForm({ ...documentForm, reminder_email: e.target.value })}
                      className="glass-input"
                      placeholder={user?.email || "your@email.com"}
                      data-testid="reminder-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400 text-sm">Remind me before expiry</Label>
                    <Select
                      value={String(documentForm.reminder_days_before)}
                      onValueChange={(value) => setDocumentForm({ ...documentForm, reminder_days_before: parseInt(value) })}
                    >
                      <SelectTrigger className="glass-input" data-testid="reminder-days">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="glass-card border-white/10">
                        <SelectItem value="7">7 days</SelectItem>
                        <SelectItem value="14">14 days</SelectItem>
                        <SelectItem value="30">1 month</SelectItem>
                        <SelectItem value="60">2 months</SelectItem>
                        <SelectItem value="90">3 months</SelectItem>
                        <SelectItem value="180">6 months</SelectItem>
                        <SelectItem value="270">9 months (passport minimum)</SelectItem>
                        <SelectItem value="365">12 months</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              
              {/* Passport 9-month warning */}
              {documentForm.document_type === "passport" && documentForm.expiry_date && (() => {
                try {
                  // Convert DD.MM.YYYY to Date
                  const isoDate = toIsoDate(documentForm.expiry_date);
                  if (!isoDate || isoDate === documentForm.expiry_date) return null;
                  const expiry = parseISO(isoDate);
                  const today = new Date();
                  const daysUntilExpiry = differenceInDays(expiry, today);
                  if (daysUntilExpiry < 270 && daysUntilExpiry > 0) {
                    return (
                      <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm text-amber-300 font-medium">Passport expiry warning</p>
                          <p className="text-xs text-amber-400/80 mt-1">
                            Many countries require passports to be valid for at least 9 months. 
                            Your passport expires in {daysUntilExpiry} days ({Math.floor(daysUntilExpiry / 30)} months).
                          </p>
                        </div>
                      </div>
                    );
                  }
                } catch { return null; }
                return null;
              })()}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDocumentModal(false)} className="btn-outline">
              Cancel
            </Button>
            <Button onClick={handleSaveDocument} className="btn-primary" disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingDocument ? "Update" : "Add Document")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Document Confirmation */}
      <AlertDialog open={showDeleteDocumentDialog} onOpenChange={setShowDeleteDocumentDialog}>
        <AlertDialogContent className="glass-card border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400">Delete Document</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Are you sure you want to delete "{documentToDelete?.document_name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-outline">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDocument}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </div>
  );
};

export default Personal;
